import { useEffect } from 'react';

const CanvasCursor = ({ containerRef }) => {
  useEffect(() => {
    let ctx;
    let e = 0;
    let pos = { x: 0, y: 0 }; // Initialize with default position
    let lines = [];
    const E = {
      debug: true,
      friction: 0.5,
      trails: 20,
      size: 50,
      dampening: 0.25,
      tension: 0.98,
    };

    function Node() {
      this.x = 0;
      this.y = 0;
      this.vy = 0;
      this.vx = 0;
    }

    function Wave(e) {
      this.init(e || {});
    }

    Wave.prototype = {
      init: function (e) {
        this.phase = e.phase || 0;
        this.offset = e.offset || 0;
        this.frequency = e.frequency || 0.001;
        this.amplitude = e.amplitude || 1;
      },
      update: function () {
        this.phase += this.frequency;
        e = this.offset + Math.sin(this.phase) * this.amplitude;
        return e;
      },
      value: function () {
        return e;
      },
    };

    function Line(e) {
      this.init(e || {});
    }

    Line.prototype = {
      init: function (e) {
        this.spring = e.spring + 0.1 * Math.random() - 0.02;
        this.friction = E.friction + 0.01 * Math.random() - 0.002;
        this.nodes = [];
        for (var n = 0; n < E.size; n++) {
          const node = new Node();
          node.x = pos.x;
          node.y = pos.y;
          this.nodes.push(node);
        }
      },
      update: function () {
        var e = this.spring;
        var t = this.nodes[0];
        t.vx += (pos.x - t.x) * e;
        t.vy += (pos.y - t.y) * e;
        
        for (var i = 0; i < this.nodes.length; i++) {
          t = this.nodes[i];
          if (i > 0) {
            var n = this.nodes[i - 1];
            t.vx += (n.x - t.x) * e;
            t.vy += (n.y - t.y) * e;
            t.vx += n.vx * E.dampening;
            t.vy += n.vy * E.dampening;
          }
          t.vx *= this.friction;
          t.vy *= this.friction;
          t.x += t.vx;
          t.y += t.vy;
          e *= E.tension;
        }
      },
      draw: function () {
        if (!ctx) return;
        
        var e, t;
        var n = this.nodes[0].x;
        var i = this.nodes[0].y;
        
        ctx.beginPath();
        ctx.moveTo(n, i);
        
        for (var a = 1; a < this.nodes.length - 1; a++) {
          e = this.nodes[a];
          t = this.nodes[a + 1];
          n = 0.5 * (e.x + t.x);
          i = 0.5 * (e.y + t.y);
          ctx.quadraticCurveTo(e.x, e.y, n, i);
        }
        
        ctx.stroke();
        ctx.closePath();
      },
    };

    function initLines() {
      lines = [];
      for (var e = 0; e < E.trails; e++) {
        lines.push(new Line({ spring: 0.45 + (e / E.trails) * 0.025 }));
      }
    }

    function handleMouseOrTouch(e) {
      const rect = containerRef.current.getBoundingClientRect();
      // clientX/Y and the rect are both relative to the viewport, so this holds after scrolling.
      // No preventDefault: touches must still scroll the page; the trails just follow along.
      const point = e.touches ? e.touches[0] : e;
      if (!point) return;
      pos.x = point.clientX - rect.left;
      pos.y = point.clientY - rect.top;
    }

    function render() {
      if (!ctx || !ctx.running) return;
      
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = '#7B49D1';
      ctx.lineWidth = 1;
      
      for (var i = 0; i < lines.length; i++) {
        lines[i].update();
        lines[i].draw();
      }
      
      ctx.frame++;
      requestAnimationFrame(render);
    }

    function resizeCanvas() {
      if (containerRef.current && ctx && ctx.canvas) {
        ctx.canvas.width = containerRef.current.offsetWidth;
        ctx.canvas.height = containerRef.current.offsetHeight;
      }
    }

    // Setup canvas
    const canvas = document.createElement('canvas');
    canvas.id = 'canvas';
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '1';
    containerRef.current.appendChild(canvas);

    // Initialize context
    ctx = canvas.getContext('2d');
    ctx.running = true;
    ctx.frame = 1;

    // Set initial position to center of container
    pos.x = containerRef.current.offsetWidth / 2;
    pos.y = containerRef.current.offsetHeight / 2;

    // Initialize lines and start rendering
    initLines();
    resizeCanvas();
    render();

    // Store container reference in a variable
    const container = containerRef.current;

    // Add event listeners
    container.addEventListener('mousemove', handleMouseOrTouch);
    container.addEventListener('touchstart', handleMouseOrTouch, { passive: true });
    container.addEventListener('touchmove', handleMouseOrTouch, { passive: true });
    window.addEventListener('resize', resizeCanvas);

    return () => {
      ctx.running = false;
      if (container) {
        container.removeChild(canvas);
        container.removeEventListener('mousemove', handleMouseOrTouch);
        container.removeEventListener('touchstart', handleMouseOrTouch, { passive: true });
        container.removeEventListener('touchmove', handleMouseOrTouch, { passive: true });
      }
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [containerRef]); // Add containerRef to dependencies

  return null;
};

export default CanvasCursor; 